import { sanitizeStoryFont } from "./storyFonts";

const Y_BY_POSITION = { top: 18, center: 50, bottom: 82 };
const SCALE_BY_SIZE = { sm: 0.75, md: 1, lg: 1.45 };

function num(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function resolveTextLayout(update = {}) {
  const hasFree =
    update.text_x != null ||
    update.text_y != null ||
    update.text_scale != null ||
    update.text_rotation != null;

  if (hasFree) {
    return {
      x: num(update.text_x, 50),
      y: num(update.text_y, 22),
      scale: num(update.text_scale, 1),
      rotation: num(update.text_rotation, 0),
    };
  }

  return {
    x: 50,
    y: Y_BY_POSITION[update.text_position] ?? 82,
    scale: SCALE_BY_SIZE[update.text_size] ?? 1,
    rotation: 0,
  };
}

export function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function newBlockId() {
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function makeTextBlock(partial = {}) {
  return {
    id: partial.id || newBlockId(),
    text: partial.text || "",
    color: partial.color || "#ffffff",
    scale: num(partial.scale, 1),
    x: num(partial.x, 50),
    y: num(partial.y, 28),
    rotation: num(partial.rotation, 0),
    font: sanitizeStoryFont(partial.font),
  };
}

export function resolveTextBlocks(update = {}) {
  const raw = Array.isArray(update.text_blocks) ? update.text_blocks : [];
  const blocks = raw
    .map((block) => makeTextBlock(block))
    .filter((block) => block.id);
  if (blocks.length) return blocks;

  const text = String(update.text || "").trim();
  if (!text) return [];
  const layout = resolveTextLayout(update);
  return [
    makeTextBlock({
      id: "legacy",
      text,
      color: update.text_color || "#ffffff",
      scale: layout.scale,
      x: layout.x,
      y: layout.y,
      font: "heebo",
    }),
  ];
}

export function blocksHaveText(blocks = []) {
  return blocks.some((block) => String(block?.text || "").trim());
}

export function blocksToPlainText(blocks = []) {
  return blocks.map((block) => String(block?.text || "").trim()).filter(Boolean).join("\n");
}

export function makeMediaBlock(partial = {}) {
  const type = partial.type === "video" ? "video" : partial.type === "gif" ? "gif" : "image";
  const isGif = type === "gif";
  return {
    id: partial.id || newBlockId(),
    type,
    url: String(partial.url || ""),
    sticker_key: String(partial.sticker_key || ""),
    x: num(partial.x, 50),
    y: num(partial.y, isGif ? 68 : 42),
    w: num(partial.w, isGif ? 46 : type === "video" ? 78 : 72),
    h: num(partial.h, isGif ? 42 : type === "video" ? 36 : 32),
    rotation: num(partial.rotation, 0),
    pan_x: num(partial.pan_x, 50),
    pan_y: num(partial.pan_y, 50),
    fit: isGif || partial.fit === "contain" ? "contain" : "cover",
    src_w: num(partial.src_w, 0),
    src_h: num(partial.src_h, 0),
  };
}

/** יחס התצוגה המקדימה (מסגרת טלפון 9:16) */
export const STORY_PREVIEW_AR = 9 / 16;

/** מסגרת גדולה ככל האפשר ששומרת על יחס המדיה — בלי לחתוך */
export function frameSizeForFullMedia(srcW, srcH, previewAR = STORY_PREVIEW_AR) {
  const mediaAR = Number(srcW) / Number(srcH);
  if (!Number.isFinite(mediaAR) || mediaAR <= 0) {
    return { w: 100, h: 100 };
  }
  if (mediaAR >= previewAR) {
    return { w: 100, h: clamp((previewAR / mediaAR) * 100, 14, 100) };
  }
  return { w: clamp((mediaAR / previewAR) * 100, 18, 100), h: 100 };
}

export function probeMediaSize(url, type) {
  if (!url) return Promise.resolve(null);
  return new Promise((resolve) => {
    const done = (width, height) => {
      if (width > 0 && height > 0) resolve({ width, height });
      else resolve(null);
    };
    if (type === "video") {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => done(video.videoWidth, video.videoHeight);
      video.onerror = () => resolve(null);
      video.src = url;
      return;
    }
    const image = new Image();
    image.onload = () => done(image.naturalWidth, image.naturalHeight);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

export function resolveMediaBlocks(update = {}) {
  const raw = Array.isArray(update.media_blocks) ? update.media_blocks : [];
  const blocks = raw.map((block) => makeMediaBlock(block)).filter((block) => block.id);
  if (blocks.length) return blocks;
  if (update.media_url && (update.media_type === "image" || update.media_type === "video")) {
    return [
      makeMediaBlock({
        id: "legacy-media",
        type: update.media_type,
        url: update.media_url,
        x: 50,
        y: 50,
        w: 100,
        h: 100,
        pan_x: update.media_x ?? 50,
        pan_y: update.media_y ?? 50,
        fit: "cover",
      }),
    ];
  }
  return [];
}

export function mediaBlocksHaveFile(blocks = []) {
  return blocks.some((block) => String(block?.url || "").trim() || String(block?.sticker_key || "").trim());
}
